import { useMemo } from 'react';
import { Outlet, Route, Routes } from 'react-router-dom';
import { RequireAuth } from '../../auth/RequireAuth';
import config from '../../config';
import { useFormConfig } from '../../hooks/useFormConfig';
import Declaration from '../../screens/DeclarationScreen/DeclarationScreen';
import DeclarationSuccessScreen from '../../screens/DeclarationSuccessScreen/DeclarationSuccessScreen';
import DefaultErrorScreen from '../../screens/ErrorScreen/DefaultErrorScreen';
import Error404Screen from '../../screens/ErrorScreen/Error404Screen';
import { FormSummary } from '../../screens/FormSummary/FormSummary';
import Overview from '../../screens/OverviewScreen/OverviewScreen';
import ResetPasswordEmailScreen from '../../screens/ResetPassword/ResetPasswordEmailScreen';
import ResetPasswordScreen from '../../screens/ResetPassword/ResetPasswordScreen';
import SigninScreen from '../../screens/SigninScreen/SigninScreen';
import SubsectionScreen from '../../screens/SubsectionScreen/SubsectionScreen';
import SummaryScreen from '../../screens/SummaryScreen/SummaryScreen';
import AccountPage from '../AccountPage/AccountPage';
import type { SubsectionRouterProps } from './SubsectionRouterProps';
const { urls } = config;

const Account = () => (
  <AccountPage>
    <Outlet />
  </AccountPage>
);

function Router() {
  const { formConfig, isLoading, error } = useFormConfig();
  const showSections = formConfig.showSections === undefined ? true : formConfig.showSections;

  const subsectionProps: SubsectionRouterProps[] = useMemo(
    () =>
      formConfig.sections?.reduce(
        (accumulator: SubsectionRouterProps[], section, sectionIndex) => [
          ...accumulator,
          ...(section?.subsections || []).map((subsection) => ({
            subsection,
            urlPath: `/${section.slug}/${subsection.slug}`,
            sectionTitle: subsection.title,
            sectionName: section.title,
            sectionIndex: sectionIndex,
          })),
        ],
        [],
      ),
    [formConfig],
  );

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  return (
    <Routes>
      <Route path={urls.signin} element={<SigninScreen />} />
      <Route path={urls.resetPasswordEmail} element={<ResetPasswordEmailScreen />} />
      <Route path={urls.resetPassword} element={<ResetPasswordScreen />} />

      <Route
        element={
          <RequireAuth>
            <Account />
          </RequireAuth>
        }
      >
        <Route path={urls.landing} element={<Overview />} />
        <Route path={urls.summary} element={<FormSummary />} />
        <Route path={urls.declaration} element={<Declaration />} />
        <Route path={urls.declarationSuccess} element={<DeclarationSuccessScreen />} />
        {subsectionProps.map((props, index) => (
          <Route path={props.urlPath} key={props.urlPath}>
            <Route path="summary" element={<SummaryScreen {...props} />} />
            <Route
              path=":page?"
              element={
                <SubsectionScreen
                  {...props}
                  sectionTitle={
                    showSections
                      ? `${props.sectionIndex !== undefined ? props.sectionIndex + 1 : ''}.${
                          index + 1
                        } ${props.sectionTitle}`
                      : `${index}. ${props.sectionTitle}`
                  }
                />
              }
            />
          </Route>
        ))}
      </Route>

      <Route path={urls.defaultError} element={<DefaultErrorScreen />} />
      <Route path="*" element={<Error404Screen />} />
    </Routes>
  );
}
export default Router;
